import React, {useCallback, useImperativeHandle, useRef} from 'react';
import {List, type ListProps} from '@mui/material';
import {
	defaultRangeExtractor,
	type Range,
	useVirtualizer,
	type VirtualItem,
	type Virtualizer,
} from '@tanstack/react-virtual';
import {
	ArrowFocusTrapContext,
	ArrowFocusTrapItemsPrimitive,
	type InternalContextDescriptor,
	useArrowFocusTrapVirtualUtils,
} from '@netx/core-components/ArrowFocusTrap';
import {useConstCallback} from '@netx/core-components/hooks/useConstCallback';
import {useHash} from '@netx/core-components/hooks/useHash';
import {useSharedRef} from '@netx/core-components/hooks/useSharedRef';
import {
	ScrollBorder,
	scrollBorderClasses,
} from '@netx/core-components/ScrollBorder';
import {styled} from '@netx/core-components/Theme';
import {appRouter} from '@netx/core-next/instance/router';
import {
	VirtualizedListItemButton,
	type VirtualizedListItemButtonProps,
} from './VirtualizedListItemButton';

const VirtualizedListItemsRoot = styled(ScrollBorder, {
	name: 'VirtualizedListItems',
})(({theme}) => ({}));

export interface VirtualizedListItemsProps extends ListProps {
	/** A `ref` to the arrow focus trap instance */
	arrowFocusTrapRef?: React.RefObject<InternalContextDescriptor>;
	items: any[];
	totalItems: number;
	renderItem: (
		virtualizedItem: VirtualItem<Element>,
		item: any,
		props: any,
	) => React.ReactNode;
	onItemClick?: (item: any) => void;
	slotProps?: {
		listItem: Partial<Omit<VirtualizedListItemButtonProps, 'key'>>;
	};
}

export const VirtualizedListItemsPrimitive = ({
	arrowFocusTrapRef: _arrowFocusTrapRef,
	items,
	totalItems,
	onItemClick,
	renderItem,
	slotProps,
	...props
}: VirtualizedListItemsProps) => {
	const ref = useRef<HTMLDivElement>(null);
	const hash = useHash();

	const lastActiveIndex = useRef(-1);

	const rowVirtualizerRef = useRef<Virtualizer<HTMLDivElement, Element>>(null);

	const arrowFocusTrapRef = useSharedRef<InternalContextDescriptor | null>(
		null,
		[_arrowFocusTrapRef],
	);

	const {rangeExtractor, handleOnActiveIndexChange} =
		useArrowFocusTrapVirtualUtils({
			arrowFocusTrapRef,
			rowVirtualizerRef,
			totalItems,
		});

	const rowVirtualizer = useVirtualizer({
		count: totalItems,
		overscan: 3,
		getScrollElement: () => ref.current,
		estimateSize: useConstCallback((index) =>
			slotProps?.listItem?.dense ? 36 : 48,
		),
		rangeExtractor,
	});

	useImperativeHandle(rowVirtualizerRef, () => rowVirtualizer, [
		rowVirtualizer,
	]);

	return (
		<ArrowFocusTrapContext
			ref={arrowFocusTrapRef}
			activeDescendantLabel={'saved-search'}
			total={totalItems}
			onItemKeyDown={(event, index) => {
				if (!onItemClick) return;
				switch (event.key) {
					case 'Enter':
					case ' ':
						event.preventDefault();
						event.stopPropagation();
						onItemClick?.(items[index]);
						return;
				}
			}}
			onActiveIndexChange={handleOnActiveIndexChange}
		>
			{({activeIndex, disabled: arrowFocusTrapDisabled, userDisabled}) => {
				return (
					<VirtualizedListItemsRoot innerRef={ref}>
						<ArrowFocusTrapItemsPrimitive disableScrollToOnFocusCapture>
							<List
								dense={slotProps?.listItem?.dense}
								{...props}
								style={{
									padding: 0,
									height: rowVirtualizer.getTotalSize(),
									...props?.style,
								}}
							>
								{rowVirtualizer.getVirtualItems().map((virtualItem) =>
									renderItem(virtualItem, items[virtualItem.index], {
										...slotProps?.listItem,
										key: items[virtualItem.index].id,
										isActive:
											!arrowFocusTrapDisabled &&
											!userDisabled &&
											activeIndex === virtualItem.index,
										style: {
											position: 'absolute',
											transform: `translatey(${virtualItem.start}px)`,
											width: '100%',
										},
									}),
								)}
							</List>
						</ArrowFocusTrapItemsPrimitive>
					</VirtualizedListItemsRoot>
				);
			}}
		</ArrowFocusTrapContext>
	);
};

export const VirtualizedListItems = VirtualizedListItemsPrimitive;

export default VirtualizedListItems;
